import { useEffect, useState } from "react";
import SectionPanel from "pages/analytics/components/SectionPanel";
import {
  EmptyMessage,
  ErrorBanner,
  TableSkeleton,
} from "pages/analytics/components/AnalyticsStates";
import {
  formatNumber,
  formatPercent,
} from "pages/analytics/components/analyticsFormatters";
import { getDestinationBreakdown } from "services/funnel-analytics.service";
import { isCustomRangeIncomplete } from "utils/analyticsQueryParams";

export default function DestinationBreakdownTable({ api, filters }) {
  const [rows, setRows] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (isCustomRangeIncomplete(filters)) {
      setIsLoading(false);
      setRows([]);
      setError(null);
      return;
    }

    let cancelled = false;

    const load = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const result = await getDestinationBreakdown(api, { filters });
        if (!cancelled) setRows(result?.rows ?? []);
      } catch {
        if (!cancelled) {
          setRows([]);
          setError("Failed to load destination data.");
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [api, filters.dateRangeType, filters.customDateRange?.[0], filters.customDateRange?.[1]]);

  if (isCustomRangeIncomplete(filters) && !isLoading) {
    return (
      <EmptyMessage message="Select start and end dates to view destination breakdown." />
    );
  }

  return (
    <SectionPanel title="Destination Breakdown" className="mb-6">
      {error && (
        <div className="mb-4">
          <ErrorBanner message={error} />
        </div>
      )}

      {isLoading ? (
        <TableSkeleton rows={4} cols={5} />
      ) : rows.length === 0 ? (
        <EmptyMessage message="No destination data available." />
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left font-medium text-gray-500">Destination</th>
                <th className="px-4 py-3 text-right font-medium text-gray-500">Visits</th>
                <th className="px-4 py-3 text-right font-medium text-gray-500">Opt-ins</th>
                <th className="px-4 py-3 text-right font-medium text-gray-500">Sales</th>
                <th className="px-4 py-3 text-right font-medium text-gray-500">Conversion</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white">
              {rows.map((row) => (
                <tr key={row.name}>
                  <td className="px-4 py-3 font-medium text-gray-900">{row.name}</td>
                  <td className="px-4 py-3 text-right text-gray-700">{formatNumber(row.visits)}</td>
                  <td className="px-4 py-3 text-right text-gray-700">{formatNumber(row.optIns)}</td>
                  <td className="px-4 py-3 text-right text-gray-700">{formatNumber(row.sales)}</td>
                  <td className="px-4 py-3 text-right text-gray-700">
                    {formatPercent(row.conversionRate)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </SectionPanel>
  );
}
